"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import TroubleShootingForCode from "./components/TroubleShootingForCode";
import Button from "antd/es/button";
import Flex from "antd/es/flex";
import Form from "antd/es/form";
import Input from "antd/es/input";
import Typography from "antd/es/typography";
import Text from "antd/es/typography/Text";
import { ComponentProps } from "./types";
import { extractErrorMessage } from "@/lib/helpers/error";
import { bookingFlowApi } from "@/lib/api/bookingFlowApi";
import type { ApiOk } from "@openings/api-client";
import { MODAL_COMPONENT_SIZE, SETUP_PAYMENT } from "./consts";
import InlineError from "./components/InlineError";
import InlineNotice from "./components/InlineNotice";
import VerificationResendButton from "./components/VerificationResendButton";
import VerificationCodeInput from "./components/VerificationCodeInput";
import { useLocale } from "@/lib/i18n";
import { useResendCooldown } from "./useResendCooldown";
import { isVerificationCodeAlreadySentError } from "./verificationSendError";
import styles from "./appointmentFlow.module.css";

const FirstClient = ({
  state,
  appointmentDetails,
  setState,
  onCreateAppointment,
}: ComponentProps) => {
  const { t } = useLocale();
  const {
    isCoolingDown: isResendCoolingDown,
    startCooldown: startResendCooldown,
  } = useResendCooldown();
  const {
    phoneNumber: statePhone,
    email: stateEmail,
    creditCardRequired,
  } = state.state;
  const verificationMethod = appointmentDetails.verificationMethod ?? "SMS";
  const isEmail = verificationMethod === "EMAIL";
  const phoneNumber = statePhone || undefined;
  const email = stateEmail;
  const [firstName, setFirstName] = useState<string>();
  const [lastName, setLastName] = useState<string>();
  const [code, setCode] = useState<string>();
  const [isSubmitting, setSubmitting] = useState(false);
  const [isSending, setSending] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({
    firstName: false,
    lastName: false,
    code: false,
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [codeAlreadySent, setCodeAlreadySent] = useState(false);
  const [showInstructure, setShowInstructure] = React.useState(false);
  const initialSendRef = useRef(false);
  const lastSentPhoneRef = useRef<string | null>(null);
  const purpose = `booking:${appointmentDetails.salonId}`;

  // Every successful send starts the cooldown, the automatic one included:
  // the server refuses another send for 45s, so an enabled Re-send could only
  // fail (BF-DRIFT-010).
  const sendVerificationCode = useCallback(async () => {
    if (isEmail) {
      if (!email) return;
    } else {
      if (!phoneNumber) return;
    }

    setErrorMessage(null);
    setSending(true);
    try {
      await bookingFlowApi.sendVerification(
        isEmail ? { email, purpose } : { phoneNumber: phoneNumber!, purpose },
      );
      setCodeAlreadySent(false);
      startResendCooldown();
    } catch (err: any) {
      // The code the customer already has still works, so this is recovery,
      // not a dead end: hold the same cooldown and say which code to enter.
      if (isVerificationCodeAlreadySentError(err)) {
        setCodeAlreadySent(true);
        startResendCooldown();
        return;
      }
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSending(false);
    }
  }, [phoneNumber, email, purpose, isEmail, startResendCooldown]);

  // Auto-send for SMS on mount; email sends on mount too since email is already known
  useEffect(() => {
    if (isEmail) {
      if (!email || initialSendRef.current) return;
      initialSendRef.current = true;
      void sendVerificationCode();
      return;
    }
    if (!phoneNumber) return;
    if (initialSendRef.current && phoneNumber === lastSentPhoneRef.current)
      return;

    initialSendRef.current = true;
    lastSentPhoneRef.current = phoneNumber;
    void sendVerificationCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneNumber, email, isEmail]);

  const handleSubmit = async () => {
    const errors = {
      firstName: !firstName?.trim(),
      lastName: !lastName?.trim(),
      code: !code?.trim(),
    };
    setFieldErrors(errors);
    if (errors.firstName || errors.lastName || errors.code) return;

    if (firstName && lastName && code) {
      setErrorMessage(null);
      setSubmitting(true);
      try {
        const customerRes = (await bookingFlowApi.createCustomerForBooking({
          ...(phoneNumber ? { phoneNumber } : {}),
          ...(email ? { email } : {}),
          firstName,
          lastName,
          salonId: appointmentDetails.salonId,
          userId: appointmentDetails.userId,
        })) as ApiOk<{ customerId: string }>;
        const { customerId } = customerRes.data;

        if (creditCardRequired) {
          setState((prev) => ({
            route: SETUP_PAYMENT,
            state: {
              ...prev.state,
              customerId,
              verificationCode: code,
            },
          }));
        } else {
          onCreateAppointment({
            customerId,
            verificationCode: code,
            errorCallback: () => {
              setSubmitting(false);
            },
          });
        }
      } catch (e) {
        setErrorMessage(extractErrorMessage(e));
        setSubmitting(false);
      }
    }
  };

  if (showInstructure) {
    return <TroubleShootingForCode onClose={() => setShowInstructure(false)} />;
  }
  return (
    <div className={styles.stepContainer}>
      <Typography.Paragraph className={styles.prompt}>
        {isEmail
          ? (t.verificationEmailSentMessage ??
            "A verification code has been sent to your email.")
          : t.verificationSentMessage}
      </Typography.Paragraph>

      <InlineNotice
        message={codeAlreadySent ? t.verificationAlreadySent : null}
      />

      <Form layout="vertical" className={styles.compactForm}>
        <Flex gap={12}>
          <Form.Item
            label={<Text strong>{t.firstName}</Text>}
            htmlFor="firstName"
            style={{ flex: 1 }}
          >
            <Input
              id="firstName"
              autoComplete="given-name"
              size={MODAL_COMPONENT_SIZE}
              placeholder={t.firstName}
              value={firstName}
              status={fieldErrors.firstName ? "error" : ""}
              onChange={(e) => {
                setFirstName(e.target.value);
                if (fieldErrors.firstName)
                  setFieldErrors((p) => ({ ...p, firstName: false }));
              }}
            />
          </Form.Item>
          <Form.Item
            label={<Text strong>{t.lastName}</Text>}
            htmlFor="lastName"
            style={{ flex: 1 }}
          >
            <Input
              id="lastName"
              autoComplete="family-name"
              size={MODAL_COMPONENT_SIZE}
              placeholder={t.lastName}
              value={lastName}
              status={fieldErrors.lastName ? "error" : ""}
              onChange={(e) => {
                setLastName(e.target.value);
                if (fieldErrors.lastName)
                  setFieldErrors((p) => ({ ...p, lastName: false }));
              }}
            />
          </Form.Item>
        </Flex>
        <Form.Item
          label={<Text strong>{t.verificationCode}</Text>}
          htmlFor="code"
        >
          <VerificationCodeInput
            id="code"
            size={MODAL_COMPONENT_SIZE}
            placeholder={t.codePlaceholder}
            value={code}
            status={fieldErrors.code ? "error" : ""}
            onChange={(digits) => {
              setCode(digits);
              if (fieldErrors.code)
                setFieldErrors((p) => ({ ...p, code: false }));
            }}
          />
        </Form.Item>
      </Form>

      <InlineError message={errorMessage} />

      <div className={styles.actionRow}>
        <Flex vertical gap={8}>
          <Button
            type="primary"
            size={MODAL_COMPONENT_SIZE}
            loading={isSubmitting}
            disabled={isSubmitting || !firstName || !lastName || !code}
            onClick={handleSubmit}
            block
          >
            {t.submit}
          </Button>
          <VerificationResendButton
            isCoolingDown={isResendCoolingDown}
            isSending={isSending}
            isSubmitting={isSubmitting}
            onResend={() => void sendVerificationCode()}
          />
        </Flex>
      </div>
    </div>
  );
};

export default FirstClient;
